import { settings } from '@/config';
import { createLogger } from '@/utils/logger';

/**
 * Drives the boot-time preloader overlay (markup lives in `index.html` so it paints
 * before the JS bundle parses — no flash of the raw canvas). It reflects asset-load
 * progress on the bar/percentage, then fades itself out once the experience signals it's
 * ready (assets loaded + warm-up render done + intro about to begin).
 *
 * Progress is EASED toward the real fraction so the bar glides smoothly instead of
 * jumping between the handful of discrete asset-loaded steps, and it never visually
 * reaches 100% until {@link finish} is called (so the last frames of decoding/compiling
 * don't leave the bar sitting full while the screen is still black).
 */
export class Preloader {
  private readonly root: HTMLElement | null;
  private readonly fill: HTMLElement | null;
  private readonly pct: HTMLElement | null;
  private readonly log = createLogger('preloader');

  private target = 0; // real fraction (0..1)
  private shown = 0; // eased fraction actually rendered
  private raf = 0;
  private finished = false;
  /** Stall watchdog — see {@link armStallWatchdog}. */
  private stallTimer: number | null = null;
  private readonly stallTimeoutMs = 30000;
  private entered = false;
  private enterBtn: HTMLButtonElement | null = null;

  /** Fired when the user clicks ENTER (Experience plays the click SFX here). The click itself is
   *  what unlocks the browser audio context, so audio started after this is reliably audible. */
  onEnter?: () => void;

  constructor() {
    this.root = document.getElementById('preloader');
    this.fill = this.root?.querySelector('.preloader__emblem-fill') ?? null;
    this.pct = this.root?.querySelector('.preloader__pct') ?? null;
    if (!this.root) this.log.warn('#preloader element not found — skipping preloader UI.');
    if (this.root) this.veil = this.buildVeil();
    this.tick = this.tick.bind(this);
    this.raf = requestAnimationFrame(this.tick);
  }

  /** Feed the current asset-load fraction (0..1). Capped at 0.92 until {@link finish}. */
  setProgress(fraction: number): void {
    this.target = Math.min(0.92, Math.max(this.target, fraction));
    this.armStallWatchdog();
  }

  /**
   * Fail-safe for a preloader that never receives {@link finish} (2026-07-19, user: "the preloader
   * stuck on 92%"). Because the bar is deliberately capped at 0.92 until `finish()`, ANY throw
   * between asset loading and that call strands the user on a frozen 92% with no feedback at all.
   *
   * This can't repair the underlying failure, but it guarantees the UI never lies: after
   * `stallTimeoutMs` with no `finish()`, it surfaces a message telling the user something went
   * wrong and to check the console — where the guarded `try/catch`es in `Experience.init` have
   * logged the real error.
   */
  private armStallWatchdog(): void {
    if (this.stallTimer !== null || this.finished) return;
    this.stallTimer = window.setTimeout(() => {
      this.stallTimer = null;
      if (this.finished) return;
      if (this.pct) this.pct.textContent = 'load stalled — see console';
      // eslint-disable-next-line no-console
      console.error(
        '[orvae:preloader] stalled before finish() — the scene failed to initialise. ' +
          'Look above for the first [orvae:*] error.',
      );
    }, this.stallTimeoutMs);
  }

  private tick(): void {
    // Ease the shown value toward the target (or 1 once finished) — a light critically-ish
    // damped follow so it never stalls but also never snaps.
    const goal = this.finished ? 1 : this.target;
    // Eased follow PLUS a minimum step: pure exponential easing advances once per rendered
    // frame, so on a slow device (heavy scene, weak GPU) the tail 92→100% could hang at "99%"
    // for many seconds — the floor caps the finish at ~20 ticks no matter the frame rate.
    const gap = goal - this.shown;
    this.shown += gap > 0 ? Math.max(gap * 0.12, Math.min(gap, 0.005)) : 0;
    if (this.finished && this.shown > 0.995) this.shown = 1;

    const pctValue = Math.round(this.shown * 100);
    // The emblem is the meter: the gradient fill rises bottom-up inside the arch mask
    // (gamified — the mark "charges" as the world loads).
    if (this.fill) this.fill.style.clipPath = `inset(${((1 - this.shown) * 100).toFixed(2)}% 0 0 0)`;
    if (this.pct) this.pct.textContent = `${pctValue}%`;
    this.root?.setAttribute('aria-valuenow', String(pctValue));

    if (this.finished && this.shown >= 1) {
      // Gated: hold on the ENTER button instead of auto-hiding (the click unlocks audio).
      if (settings.ui.enterGate && !this.entered) {
        this.showEnter();
        return; // stop the rAF; the click drives the rest
      }
      this.hide();
      return;
    }
    this.raf = requestAnimationFrame(this.tick);
  }

  /** Reveal the ENTER button once the bar is full. Clicking it hides the preloader. */
  private showEnter(): void {
    if (this.enterBtn || !this.root) return;
    cancelAnimationFrame(this.raf);
    // THE GLIMPSE (2026-07-21, user: "in the preloader you can see a little bit — the emblem,
    // through fog and clouds"). Loading is done and the render loop is live behind this overlay,
    // so it's finally safe to thin the opaque white into a haze: the scene at frame 0 appears
    // faintly through mist, with the cloud puffs drifting idly — "the camera is in the clouds".
    this.root.classList.add('is-glimpse', 'is-ready');
    this.wakeVeil();
    const btn = document.createElement('button');
    btn.className = 'preloader__enter';
    btn.innerHTML = `<span class="preloader__enter-label">${settings.ui.enterLabel}</span><span class="preloader__enter-arrow" aria-hidden="true"><svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M5 12h14M13 6l6 6-6 6"/></svg></span>`;
    btn.addEventListener(
      'click',
      () => {
        this.entered = true;
        this.onEnter?.(); // click SFX + audio unlock happen here
        this.hide();
      },
      { once: true },
    );
    (this.root.querySelector('.preloader__inner') ?? this.root).appendChild(btn);
    requestAnimationFrame(() => btn.classList.add('is-shown'));
  }

  /**
   * Signal that loading is done — the bar fills to 100% and the overlay fades out. Returns
   * a promise that resolves after the fade completes (so callers can sequence the intro).
   */
  finish(): Promise<void> {
    this.finished = true;
    if (this.stallTimer !== null) {
      window.clearTimeout(this.stallTimer);
      this.stallTimer = null;
    }
    return new Promise((resolve) => {
      const done = (): void => {
        this.root?.removeEventListener('transitionend', done);
        resolve();
      };
      // NOTE: no blanket timeout when GATED — the overlay waits on the user's ENTER click, which
      // can take any amount of time. (A global fallback here would resolve early and start the
      // intro behind the still-visible preloader.) The fallback is armed once hiding begins.
      if (!settings.ui.enterGate) window.setTimeout(done, 1100);
      // `hide()` (adding `.is-hidden`) fires from `tick`, or from the ENTER click when gated.
      const wait = (): void => {
        if (this.root?.classList.contains('is-hidden')) {
          this.root.addEventListener('transitionend', done, { once: true });
          window.setTimeout(done, 1200); // safety, now that the fade has actually started
        } else {
          window.setTimeout(wait, 60);
        }
      };
      wait();
    });
  }

  /**
   * The cloud veil under the preloader — the "camera starts inside the clouds" illusion
   * (2026-07-21). Built once at boot; {@link hide} sends every puff drifting outward while the
   * veil's white base clears, then the layer removes itself. Randomised sizes/positions/drift
   * so each visit parts slightly differently.
   */
  private buildVeil(): HTMLDivElement {
    const veil = document.createElement('div');
    veil.id = 'cloud-veil';
    for (let i = 0; i < 7; i += 1) {
      const puff = document.createElement('div');
      puff.className = 'veil-puff';
      const size = 34 + Math.random() * 46; // vmin
      puff.style.width = `${size}vmin`;
      puff.style.height = `${size * (0.72 + Math.random() * 0.3)}vmin`;
      puff.style.left = `${-12 + Math.random() * 104}%`;
      puff.style.top = `${-12 + Math.random() * 104}%`;
      // Stash the parting drift on the element — applied when hide() parts the veil.
      puff.dataset.dx = String((Math.random() - 0.5) * 70);
      puff.dataset.dy = String((Math.random() - 0.5) * 55);
      veil.appendChild(puff);
    }
    // display:none until the parting actually starts — the veil sits under the OPAQUE preloader
    // during loading, so compositing its blurred layers there is pure waste (it visibly tanked
    // weak machines: seven full-screen blurs per frame for nothing).
    veil.style.display = 'none';
    document.body.appendChild(veil);
    return veil;
  }
  private veil: HTMLDivElement | null = null;

  /** Materialise the veil with a slow idle drift — only called once the scene renders behind
   *  the overlay (compositing the blurred puffs before that was pure waste; see buildVeil). */
  private wakeVeil(): void {
    if (!this.veil) return;
    this.veil.style.display = '';
    for (const el of Array.from(this.veil.querySelectorAll<HTMLElement>('.veil-puff'))) {
      const dur = 7 + Math.random() * 6;
      const delay = -Math.random() * dur; // negative: start mid-cycle, no synchronized pulse
      el.style.animation = `veil-drift ${dur.toFixed(1)}s ease-in-out ${delay.toFixed(1)}s infinite alternate`;
    }
  }

  private hide(): void {
    cancelAnimationFrame(this.raf);
    this.root?.classList.add('is-hidden');
    // Part the cloud veil: each puff drifts along its own randomised direction while scaling
    // up, and the white base clears — the scene reads as the camera emerging through clouds.
    // The layer removes itself once the slowest transition is done.
    if (this.veil) {
      const veil = this.veil;
      this.veil = null;
      veil.style.display = ''; // usually already woken by the glimpse; direct hide() path needs it
      // Two frames so the layer paints at rest once; only then start the transitions
      // (a transition applied in the same frame as display:none → block never animates).
      requestAnimationFrame(() =>
        requestAnimationFrame(() => {
          for (const el of Array.from(veil.querySelectorAll<HTMLElement>('.veil-puff'))) {
            // A running CSS animation OWNS the transform property — cancel it or the parting
            // transform below would be silently ignored.
            el.style.animation = 'none';
            el.style.transform = `translate(${el.dataset.dx}vmin, ${el.dataset.dy}vmin) scale(1.9)`;
          }
          veil.classList.add('is-parting');
        }),
      );
      window.setTimeout(() => veil.remove(), 3100);
    }
  }

  dispose(): void {
    cancelAnimationFrame(this.raf);
    if (this.stallTimer !== null) {
      window.clearTimeout(this.stallTimer);
      this.stallTimer = null;
    }
    this.root?.remove();
  }
}
